import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

/**
 * Hook customizado para gerenciamento de usuários
 * Centraliza toda a lógica de API (fetch, create, update, delete) com AbortController
 * 
 * @param {number|null} activeCondominio - ID do condomínio ativo
 * @returns {Object} { usuarios, loading, error, fetchUsuarios, createUsuario, updateUsuario, deleteUsuario }
 */
const useUsuarios = (activeCondominio) => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const abortControllerRef = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL;
  
  // Cleanup: cancelar requisições pendentes ao desmontar
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);
  
  /**
   * Buscar lista de usuários do condomínio ativo
   */
  const fetchUsuarios = useCallback(async () => {
    if (!activeCondominio) return;
    
    // Cancelar requisições anteriores e criar nova
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${apiUrl}/api/usuarios`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "x-condominio-id": activeCondominio
        },
        signal: abortControllerRef.current.signal
      });
      
      setUsuarios(response.data.dados || []);
    } catch (err) {
      // Não exibir erro se foi cancelado (desmontagem ou mudança de condominio)
      if (err.name === 'CanceledError') return;
      
      console.error("Erro ao buscar usuários:", err);
      setError(err.response?.data?.mensagem || "Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  }, [activeCondominio, apiUrl]);
  
  // Buscar usuários quando condomínio muda
  useEffect(() => {
    if (activeCondominio) {
      fetchUsuarios();
    }
  }, [activeCondominio, fetchUsuarios]);
  
  /**
   * Criar novo usuário
   * @param {Object} dadosUsuario - { username, password, cargo, condominioIds }
   * @returns {Promise<void>}
   * @throws {Error} Se houver erro na criação
   */
  const createUsuario = async (dadosUsuario) => {
    const token = localStorage.getItem("token");
    
    await axios.post(
      `${apiUrl}/api/usuarios`,
      dadosUsuario,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "x-condominio-id": activeCondominio
        },
        signal: abortControllerRef.current?.signal
      }
    );
    
    // Recarregar lista após criação
    await fetchUsuarios();
  };
  
  /**
   * Atualizar usuário existente
   * @param {number} usuarioId - ID do usuário
   * @param {Object} dadosUsuario - { username, cargo, password (opcional) }
   * @returns {Promise<void>}
   * @throws {Error} Se houver erro na atualização
   */
  const updateUsuario = async (usuarioId, dadosUsuario) => {
    const token = localStorage.getItem("token");
    
    await axios.put(
      `${apiUrl}/api/usuarios/${usuarioId}`,
      dadosUsuario,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "x-condominio-id": activeCondominio
        },
        signal: abortControllerRef.current?.signal
      }
    );
    
    // Recarregar lista após atualização
    await fetchUsuarios();
  };
  
  /**
   * Deletar usuário (soft delete)
   * @param {number} usuarioId - ID do usuário
   * @returns {Promise<void>}
   * @throws {Error} Se houver erro na exclusão
   */
  const deleteUsuario = async (usuarioId) => {
    const token = localStorage.getItem("token");
    
    await axios.delete(`${apiUrl}/api/usuarios/${usuarioId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "x-condominio-id": activeCondominio
      },
      signal: abortControllerRef.current?.signal
    });
    
    // Recarregar lista após exclusão
    await fetchUsuarios();
  };
  
  return {
    usuarios,
    loading,
    error,
    fetchUsuarios,
    createUsuario,
    updateUsuario,
    deleteUsuario
  };
};

export default useUsuarios;
